import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const BACKEND_URL = process.env.NODE_ENV === 'production'
    ? 'https://learnify-58mi.onrender.com'
    : 'http://localhost:5000';

  const navLinks = useMemo(
    () => [
      { to: '/', label: 'Home' },
      { to: '/about', label: 'About' },
      { to: '/courses', label: 'Courses' },
      { to: '/team', label: 'Instructors' },
      { to: '/categories', label: 'Categories' },
      { to: '/contact', label: 'Contact' }
    ],
    []
  );

  // Get avatar URL
  const getAvatarUrl = () => {
    if (user?.profileImage && user.profileImage !== '/default-profile.png') {
      return `${BACKEND_URL}${user.profileImage}?t=${Date.now()}`;
    }
    return `https://api.dicebear.com/8.x/micah/svg?seed=${user?._id}`;
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    navigate('/auth');
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    };
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        setIsProfileOpen(false);
        const toggle = document.querySelector('.fundisha-nav-toggle');
        if (toggle) toggle.focus();
      }

      if (isMenuOpen && event.key === 'Tab') {
        const container = document.querySelector('.fundisha-nav-main');
        if (!container) return;
        const focusable = container.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);
  return (
    <nav className="fundisha-navbar">
      <div className="fundisha-navbar-inner">
        <Link to="/" className="fundisha-brand" onClick={handleNavClick}>
          Fundisha<span className="fundisha-brand-dot">.</span>
        </Link>

        <button
          className="fundisha-nav-toggle"
          type="button"
          aria-label="Open navigation menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          <i className={`fa-solid ${isMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
        </button>

        <div
          className={`fundisha-nav-main ${isMenuOpen ? 'open' : ''}`}
          role="dialog"
          aria-modal={isMenuOpen}
          aria-hidden={!isMenuOpen}
        >
          <div className="fundisha-nav-links">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`fundisha-nav-link ${location.pathname === link.to ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="fundisha-nav-actions">
            {user ? (
              <div className="fundisha-nav-user">
                <button
                  className="fundisha-nav-user-btn"
                  type="button"
                  aria-label="Toggle profile menu"
                  onClick={() => setIsProfileOpen((current) => !current)}
                >
                  <img src={getAvatarUrl()} alt="avatar" className="fundisha-avatar" />
                  <span>{user.name || 'Profile'}</span>
                </button>

                {isProfileOpen && (
                  <div className="fundisha-nav-user-menu">
                    <Link to="/dashboard" className="fundisha-nav-user-item" onClick={handleNavClick}>
                      Dashboard
                    </Link>
                    <Link to="/profile" className="fundisha-nav-user-item" onClick={handleNavClick}>
                      Profile
                    </Link>
                    <button type="button" className="fundisha-nav-user-item" onClick={handleLogout}>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className="fundisha-btn-primary" onClick={handleNavClick}>
                Join Now
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;